import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { authFetch } from "../utils/authFetch";

const LUNI = [
  { val: 1, label: "01 - Ianuarie" },
  { val: 2, label: "02 - Februarie" },
  { val: 3, label: "03 - Martie" },
  { val: 4, label: "04 - Aprilie" },
  { val: 5, label: "05 - Mai" },
  { val: 6, label: "06 - Iunie" },
  { val: 7, label: "07 - Iulie" },
  { val: 8, label: "08 - August" },
  { val: 9, label: "09 - Septembrie" },
  { val: 10, label: "10 - Octombrie" },
  { val: 11, label: "11 - Noiembrie" },
  { val: 12, label: "12 - Decembrie" },
];

export default function Balanta() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  // Period mode: 'month' (select month/year) or 'range' (date1 - date2)
  const [periodMode, setPeriodMode] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Date range states
  const [from, setFrom] = useState(new Date(currentYear, currentMonth - 1, 1));
  const [to, setTo] = useState(new Date(currentYear, currentMonth, 0));

  const [tip, setTip] = useState("analitica");
  const [searchQuery, setSearchQuery] = useState("");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // Sync dates when month/year changes in 'month' mode
  useEffect(() => {
    if (periodMode === "month") {
      const f = new Date(selectedYear, selectedMonth - 1, 1);
      const t = new Date(selectedYear, selectedMonth, 0); // Last day of month
      setFrom(f);
      setTo(t);
    }
  }, [periodMode, selectedMonth, selectedYear]);

  const formatDateString = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const fromStr = formatDateString(from);
      const toStr = formatDateString(to);
      const res = await authFetch(`/rapoarte/balanta?from=${fromStr}&to=${toStr}&tip=${tip}`);
      if (Array.isArray(res)) {
        setRows(res);
      } else {
        setRows([]);
        setErr("Format răspuns invalid de la server.");
      }
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare balanță de verificare.");
    } finally {
      setBusy(false);
    }
  };

  // Reload report when dates or tip changes
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, tip]);

  const fmt2 = (n) => {
    const val = Number(n ?? 0);
    if (Math.abs(val) < 0.005) return "-";
    return val.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Filter rows based on search query
  const filteredRows = useMemo(() => {
    const s = searchQuery.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      if (r.is_total_general || r.is_group_total || r.Cont === "Total") return true;
      const c = String(r.Cont ?? "").toLowerCase();
      const d = String(r.Denumire ?? "").toLowerCase();
      return c.includes(s) || d.includes(s);
    });
  }, [rows, searchQuery]);

  const handlePrint = () => {
    window.print();
  };

  const yearsList = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className="p-3">
      <Card className="shadow-sm border-0 rounded-4 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h4 className="fw-bold text-dark mb-1">Balanță de Verificare</h4>
            <p className="text-muted small mb-0">
              Generează și analizează balanța de verificare contabilă a societății.
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={handlePrint} className="rounded-3 fw-semibold">
              Printează
            </Button>
            <Button variant="primary" onClick={load} disabled={busy} className="rounded-3 fw-semibold">
              {busy ? "Se încarcă..." : "Reîncarcă"}
            </Button>
          </div>
        </div>

        {/* Period selection Controls */}
        <Row className="g-3 mb-4 align-items-end bg-light p-3 rounded-3 mx-0 border border-light-subtle">
          <Col xs={12} sm={6} md={3}>
            <Form.Label className="fw-semibold text-secondary mb-1">Mod Selectare Perioadă</Form.Label>
            <Form.Select
              value={periodMode}
              onChange={(e) => setPeriodMode(e.target.value)}
              className="rounded-3 border-light-subtle shadow-none bg-white"
            >
              <option value="month">Selectare după Lună (ex. Luna 8)</option>
              <option value="range">Interval de date (De la - Până la)</option>
            </Form.Select>
          </Col>

          {periodMode === "month" ? (
            <>
              <Col xs={12} sm={6} md={3}>
                <Form.Label className="fw-semibold text-secondary mb-1">Luna</Form.Label>
                <Form.Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="rounded-3 border-light-subtle shadow-none bg-white"
                >
                  {LUNI.map((l) => (
                    <option key={l.val} value={l.val}>
                      {l.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col xs={12} sm={6} md={2}>
                <Form.Label className="fw-semibold text-secondary mb-1">Anul</Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="rounded-3 border-light-subtle shadow-none bg-white"
                >
                  {yearsList.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </>
          ) : (
            <Col xs={12} sm={6} md={5}>
              <Form.Label className="fw-semibold text-secondary mb-1">Interval Date</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <DatePicker
                  selected={from}
                  onChange={(date) => setFrom(date)}
                  dateFormat="dd.MM.yyyy"
                  className="form-control rounded-3 border-light-subtle shadow-none bg-white"
                />
                <span className="text-secondary fw-bold">-</span>
                <DatePicker
                  selected={to}
                  onChange={(date) => setTo(date)}
                  dateFormat="dd.MM.yyyy"
                  className="form-control rounded-3 border-light-subtle shadow-none bg-white"
                />
              </div>
            </Col>
          )}

          <Col xs={12} sm={6} md={4}>
            <Form.Label className="fw-semibold text-secondary mb-1">Tip Balanță</Form.Label>
            <Form.Select
              value={tip}
              onChange={(e) => setTip(e.target.value)}
              className="rounded-3 border-light-subtle shadow-none bg-white"
            >
              <option value="analitica">Balanță conturi analitice</option>
              <option value="sintetica">Balanță conturi sintetice</option>
              <option value="grupe">Balanță cu totaluri pe grupe</option>
            </Form.Select>
          </Col>

          <Col xs={12} sm={6} md={5}>
            <Form.Label className="fw-semibold text-secondary mb-1">Căutare Cont / Denumire</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ex: 401, 5121, Furnizori..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="rounded-3 border-light-subtle shadow-none bg-white"
            />
          </Col>

          <Col xs={12} sm={6} md={3} className="text-md-end text-sm-start ms-auto">
            <span className="text-muted small fw-semibold">
              {filteredRows.filter((r) => !r.is_total_general && !r.is_group_total).length} conturi afișate
            </span>
          </Col>
        </Row>

        {err && (
          <Alert variant="danger" className="rounded-3 mb-4">
            {err}
          </Alert>
        )}

        <div className="table-responsive rounded-3 border border-light-subtle">
          {busy ? (
            <div className="py-5 text-center">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2 mb-0">Se prelucrează balanța contabilă...</p>
            </div>
          ) : (
            <Table hover bordered size="sm" className="align-middle mb-0 text-nowrap" style={{ fontSize: "0.85rem" }}>
              <thead>
                <tr className="bg-primary text-white text-center align-middle">
                  <th rowSpan="2" style={{ width: "90px" }} className="py-2 bg-primary text-white border-bottom-0">Cont</th>
                  <th rowSpan="2" className="py-2 bg-primary text-white border-bottom-0">Denumire Cont</th>
                  <th colSpan="2" className="py-2 bg-primary text-white border-bottom-0">Solduri Inițiale</th>
                  <th colSpan="2" className="py-2 bg-primary text-white border-bottom-0">Rulaje Cumulate</th>
                  <th colSpan="2" className="py-2 bg-primary text-white border-bottom-0">Rulaje Luna</th>
                  <th colSpan="2" className="py-2 bg-primary text-white border-bottom-0">Total Sume</th>
                  <th colSpan="2" className="py-2 bg-primary text-white border-bottom-0">Solduri Finale</th>
                </tr>
                <tr className="text-center align-middle bg-primary-subtle text-dark">
                  <th className="py-1">Debit</th>
                  <th className="py-1">Credit</th>
                  <th className="py-1">Debit</th>
                  <th className="py-1">Credit</th>
                  <th className="py-1">Debit</th>
                  <th className="py-1">Credit</th>
                  <th className="py-1">Debit</th>
                  <th className="py-1">Credit</th>
                  <th className="py-1">Debit</th>
                  <th className="py-1">Credit</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center text-muted py-4">
                      Nu s-au găsit înregistrări contabile active în perioada selectată.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, idx) => {
                    const isTotal = r.is_total_general || r.Cont === "Total";
                    const isGroup = r.is_group_total;

                    let rowClass = "";
                    if (isTotal) rowClass = "table-secondary fw-bold border-top border-dark-subtle";
                    else if (isGroup) rowClass = "table-light fw-semibold text-primary";

                    return (
                      <tr key={idx} className={rowClass}>
                        <td className={`font-monospace ${isTotal || isGroup ? "fw-bold" : ""}`}>{r.Cont}</td>
                        <td>{r.Denumire}</td>

                        {/* Solduri Inițiale */}
                        <td className="text-end">{fmt2(r.SoldIniDebit)}</td>
                        <td className="text-end">{fmt2(r.SoldIniCredit)}</td>

                        {/* Rulaje Cumulate */}
                        <td className="text-end">{fmt2(r.RulajDebit)}</td>
                        <td className="text-end">{fmt2(r.RulajCredit)}</td>

                        {/* Rulaje Luna */}
                        <td className="text-end">{fmt2(r.RulajLunaDebit)}</td>
                        <td className="text-end">{fmt2(r.RulajLunaCredit)}</td>

                        {/* Total Sume */}
                        <td className="text-end">{fmt2(r.CumulatDebit)}</td>
                        <td className="text-end">{fmt2(r.CumulatCredit)}</td>

                        {/* Solduri Finale */}
                        <td className="text-end">{fmt2(r.SoldDebit)}</td>
                        <td className="text-end">{fmt2(r.SoldCredit)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
}
